/* eslint-disable max-lines-per-function */
import { Abschnitt } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt';
import { Arbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/entities/Arbeitsbereich';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { Mitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Mitarbeiter';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import {
  AbwesenheitsStation,
  ArbeitsbereichStation,
} from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { ROTATIONSPLAN_BY_ID } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanById/graphql/queries';
import {
  Mitarbeiter_RotationsplanByIdQuery,
  Mitarbeiter_RotationsplanByIdQueryVariables,
} from '@/gql/graphql';
import { useSuspenseQuery } from '@apollo/client/react';
import * as Sentry from '@sentry/react';

export function useRotationsplanById(
  standortIds: string[],
  rotationsplanId: string,
): {
  rotationsplan?: Rotationsplan;
  error?: string;
} {
  const result = useSuspenseQuery<
    Mitarbeiter_RotationsplanByIdQuery,
    Mitarbeiter_RotationsplanByIdQueryVariables
  >(ROTATIONSPLAN_BY_ID, {
    variables: { standortIds, rotationsplanId },
  });

  if (result.error) {
    Sentry.captureException(result.error);
    return { error: result.error.message };
  }

  if (!result.data.mitarbeiter_rotationsplanById) {
    return { rotationsplan: undefined };
  }

  const abschnitte = result.data.mitarbeiter_rotationsplanById.abschnitte.map(
    (abschnitt) => {
      const stationen = abschnitt.stationen.map((station) => {
        if (
          station.__typename ===
          'mitarbeiter_rotationsplanArbeitsbereichStation'
        ) {
          return new ArbeitsbereichStation({
            id: station.id,
            abschnittId: abschnitt.id,
            von: new Date(station.von),
            bis: new Date(station.bis),
            arbeitsbereich: new Arbeitsbereich({
              id: station.arbeitsbereich.id,
              markierung: station.arbeitsbereich.markierung,
              name: station.arbeitsbereich.name,
              standort: new Standort({
                id: station.arbeitsbereich.standort.id,
                name: station.arbeitsbereich.standort.name,
                storenummer: station.arbeitsbereich.standort.storenummer,
              }),
            }),
            lernziele: station.lernziele,
            bereichsleiter: station.bereichsleiter
              ? new Mitarbeiter({
                  id: station.bereichsleiter.id,
                  vorname: station.bereichsleiter.vorname,
                  nachname: station.bereichsleiter.nachname,
                  imgUrl: station.bereichsleiter.bildUrl,
                })
              : undefined,
          });
        } else {
          return new AbwesenheitsStation({
            id: station.id,
            abschnittId: abschnitt.id,
            von: new Date(station.von),
            bis: new Date(station.bis),
            titel: station.name,
          });
        }
      });

      return new Abschnitt({
        id: abschnitt.id,
        name: abschnitt.name,
        position: abschnitt.position,
        stationen,
      });
    },
  );

  return {
    rotationsplan: new Rotationsplan({
      id: result.data.mitarbeiter_rotationsplanById.id,
      standortId: result.data.mitarbeiter_rotationsplanById.standortId,
      mitarbeiter: new Mitarbeiter({
        id: result.data.mitarbeiter_rotationsplanById.mitarbeiter.id,
        vorname: result.data.mitarbeiter_rotationsplanById.mitarbeiter.vorname,
        nachname:
          result.data.mitarbeiter_rotationsplanById.mitarbeiter.nachname,
        imgUrl:
          result.data.mitarbeiter_rotationsplanById.mitarbeiter.bildUrl ??
          undefined,
        arbeitsbereich: result.data.mitarbeiter_rotationsplanById.mitarbeiter
          .arbeitsbereich
          ? new Arbeitsbereich({
              id: result.data.mitarbeiter_rotationsplanById.mitarbeiter
                .arbeitsbereich.id,
              markierung:
                result.data.mitarbeiter_rotationsplanById.mitarbeiter
                  .arbeitsbereich.markierung,
              name: result.data.mitarbeiter_rotationsplanById.mitarbeiter
                .arbeitsbereich.name,
              standort: new Standort({
                id: result.data.mitarbeiter_rotationsplanById.mitarbeiter
                  .arbeitsbereich.standort.id,
                name: result.data.mitarbeiter_rotationsplanById.mitarbeiter
                  .arbeitsbereich.standort.name,
                storenummer:
                  result.data.mitarbeiter_rotationsplanById.mitarbeiter
                    .arbeitsbereich.standort.storenummer,
              }),
            })
          : undefined,
      }),
      beruf: new Beruf({
        id: result.data.mitarbeiter_rotationsplanById.beruf.id,
        name: result.data.mitarbeiter_rotationsplanById.beruf.name,
        markierung: result.data.mitarbeiter_rotationsplanById.beruf.markierung,
        abkuerzung:
          result.data.mitarbeiter_rotationsplanById.beruf.abkuerzung ?? '',
        istAusbildung:
          result.data.mitarbeiter_rotationsplanById.beruf.istAusbildung,
      }),
      abschnitte,
    }),
  };
}
